<template>
  <Menu as="div" class="z-30">
    <MenuButton
      data-cy="open-stream-options"
      class="flex justify-center items-center text-sm font-medium rounded-full text-white"
    >
      <CogIcon class="h-6 w-6 shadow-filter" />
    </MenuButton>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <MenuItems
        as="div"
        class="
          origin-top-right
          absolute
          right-8
          bottom-2
          mt-1
          w-64
          rounded-md
          shadow-lg
          bg-white
          ring-1 ring-black ring-opacity-5
          focus:outline-none
          z-10
          text-gray-700
        "
      >
        <div class="py-1">
          <MenuItem v-if="streamName">
            <span class="font-semibold block px-4 py-2 text-sm">{{ streamName }}</span>
          </MenuItem>

          <MenuItem>
            <span
              data-cy="toggle-stream-preview"
              class="
                cursor-pointer
                hover:bg-gray-100
                text-gray-900
                group
                flex
                w-full
                items-center
                rounded-md
                px-4
                py-2
                text-sm
              "
              @click="$emit('togglePreviewOverlay')"
            >
              <template v-if="hidePreview">
                <EyeOffIcon class="w-6 h-6 mr-2 text-vice-dark-grey text-sm" />
                Hide in video grid
              </template>
              <template v-else>
                <EyeIcon class="w-6 h-6 mr-2 text-vice-dark-grey text-sm" />
                Show in video grid
              </template>
            </span>
          </MenuItem>

          <MenuItem v-if="showSwitchDevice">
            <span
              data-cy="change-camera-source"
              class="
                cursor-pointer
                hover:bg-gray-100
                text-gray-900
                group
                flex
                w-full
                items-center
                rounded-md
                px-4
                py-2
                text-sm
              "
              @click="$emit('changeCameraSource')"
            >
              <SwitchHorizontalIcon class="w-6 h-6 mr-2 text-vice-dark-grey text-sm" />
              Switch camera
            </span>
          </MenuItem>

          <MenuItem v-if="showDisconnectDevice">
            <span
              data-cy="disconnect-device"
              class="
                cursor-pointer
                hover:bg-gray-100
                text-gray-900
                group
                flex
                w-full
                items-center
                rounded-md
                px-4
                py-2
                text-sm
              "
              @click="$emit('disconnectDevice')"
            >
              <SignalSlashIcon class="w-6 h-6 mr-2 text-vice-dark-grey text-sm" />
              Disconnect Device
            </span>
          </MenuItem>

          <MenuItem v-if="streamName === 'Smartglasses'">
            <span
              data-cy="toggle-vuzix-audio"
              class="
                cursor-pointer
                hover:bg-gray-100
                text-gray-900
                group
                flex
                w-full
                items-center
                rounded-md
                px-4
                py-2
                text-sm
              "
              @click="$emit('toggleVuzixAudio')"
            >
              <ViceMicrophone
                :muted="isAudioMuted"
                :pathFill="`#606367`"
                :lineTwoStroke="`#606367`"
                class="w-6 h-6 mr-2 text-vice-dark-grey text-sm"
              />
              <span v-if="isAudioMuted">Unmute</span>
              <span v-else>Mute</span>
            </span>
          </MenuItem>

          <MenuItem v-if="streamName === 'Smartglasses'">
            <span
              data-cy="toggle-external-audio-io"
              class="
                cursor-pointer
                hover:bg-gray-100
                text-gray-900
                group
                flex
                w-full
                items-center
                rounded-md
                px-4
                py-2
                text-sm
              "
              @click="$emit('toggleExternalAudioIO')"
            >
              <template v-if="!externalAudioIO">
                <HeadsetOn class="w-6 h-6 mr-2" />
                Use as default mic and speaker
              </template>
              <template v-else>
                <HeadsetOff class="w-6 h-6 mr-2" />
                Stop using as default mic and speaker
              </template>
            </span>
          </MenuItem>
        </div>
      </MenuItems>
    </transition>
  </Menu>
</template>

<script>
import { EyeOffIcon, EyeIcon, SwitchHorizontalIcon } from '@heroicons/vue/outline';
import { CogIcon } from '@heroicons/vue/solid';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import SignalSlashIcon from '@/Components/Icons/SignalSlashIcon.vue';
import ViceMicrophone from '@/Components/Icons/ViceMicrophone.vue';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import HeadsetOn from '@/Components/Icons/HeadsetOn.vue';
import HeadsetOff from '@/Components/Icons/HeadsetOff.vue';

export default {
  methods: { useLocalStreamingDevicesStore },
  props: {
    streamName: {
      required: false,
    },
    hidePreview: {
      default: true,
    },
    showDisconnectDevice: {
      default: false,
    },
    showSwitchDevice: {
      default: false,
    },
    isAudioMuted: {
      default: true,
    },
    externalAudioIO: {
      default: true,
    },
  },
  emits: [
    'togglePreviewOverlay',
    'disconnectDevice',
    'changeCameraSource',
    'toggleVuzixAudio',
    'toggleExternalAudioIO',
  ],
  components: {
    HeadsetOff,
    HeadsetOn,
    ViceMicrophone,
    EyeIcon,
    EyeOffIcon,
    CogIcon,
    SwitchHorizontalIcon,
    SignalSlashIcon,
    Menu,
    MenuItem,
    MenuItems,
    MenuButton,
  },
  setup() {
    return {};
  },
};
</script>
